import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';
import { 
  Trophy, 
  Users, 
  Calendar, 
  TrendingUp, 
  ArrowRight, 
  Flag, 
  Zap, 
  Target,
  ChevronRight,
  Play
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);

  const features = [
    {
      icon: Trophy,
      title: t('home.featureBrackets') || "Tournament Brackets",
      description: t('home.featureBracketsDesc') || "Create and manage tournament brackets with automatic advancement and seeding."
    },
    {
      icon: Users,
      title: t('home.featureAge') || "Age Categories",
      description: t('home.featureAgeDesc') || "Organize teams by U8, U10, U12, U14, U16, U18, and Adult divisions."
    },
    {
      icon: Calendar,
      title: t('home.featureScheduling') || "Field Scheduling",
      description: t('home.featureSchedulingDesc') || "Assign games to fields and manage schedules efficiently."
    },
    {
      icon: TrendingUp,
      title: t('home.featureComparison') || "Team Comparison",
      description: t('home.featureComparisonDesc') || "Compare teams side-by-side with detailed performance analytics."
    },
    {
      icon: Zap,
      title: t('home.featureAI') || "AI Predictions",
      description: t('home.featureAIDesc') || "GPT-5.2 powered game predictions based on team performance."
    },
    {
      icon: Target,
      title: t('home.featureScoring') || "Score Tracking",
      description: t('home.featureScoringDesc') || "Real-time score reporting and automatic stat updates."
    }
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_tourneytracker-4/artifacts/r7gbctid_231017112446-01-flag-football-spotlight.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8 stagger-fade-in">
              <Badge variant="secondary" className="w-fit" data-testid="hero-badge">
                <Zap className="w-3 h-3 mr-1" />
                {t('home.tagline')}
              </Badge>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-none">
                {t('home.heroLine1') || 'RUN YOUR'}
                <span className="block text-primary">{t('home.heroLine2') || 'FLAG FOOTBALL'}</span>
                <span className="block">{t('home.heroLine3') || 'TOURNAMENTS'}</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
                {t('home.heroDescription') || 'The complete platform for organizing flag football tournaments. Manage brackets, track scores, take payments, and get AI-powered game predictions.'}
              </p>
              
              <div className="flex flex-wrap gap-4">
                {user ? (
                  <Link to="/dashboard">
                    <Button size="lg" className="btn-primary gap-2" data-testid="go-to-dashboard-btn">
                      {t('home.goToDashboard') || 'Go to Dashboard'}
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/register">
                      <Button size="lg" className="btn-primary gap-2" data-testid="get-started-btn">
                        {t('home.getStarted')}
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to="/tournaments">
                      <Button size="lg" variant="outline" className="gap-2" data-testid="view-tournaments-btn">
                        <Play className="w-5 h-5" />
                        {t('home.viewTournaments') || 'View Tournaments'}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              <Card className="glass card-hover" data-testid="stat-tournaments">
                <CardContent className="p-6">
                  <Trophy className="w-8 h-8 text-primary mb-4" />
                  <p className="text-4xl font-black">{stats?.total_tournaments || 0}</p>
                  <p className="text-sm text-muted-foreground">{t('tournament.title')}</p>
                </CardContent>
              </Card>
              
              <Card className="glass card-hover" data-testid="stat-teams">
                <CardContent className="p-6">
                  <Users className="w-8 h-8 text-primary mb-4" />
                  <p className="text-4xl font-black">{stats?.total_teams || 0}</p>
                  <p className="text-sm text-muted-foreground">Teams</p>
                </CardContent>
              </Card>
              
              <Card className="glass card-hover" data-testid="stat-games">
                <CardContent className="p-6">
                  <Calendar className="w-8 h-8 text-accent mb-4" />
                  <p className="text-4xl font-black">{stats?.total_games || 0}</p>
                  <p className="text-sm text-muted-foreground">Games Played</p>
                </CardContent>
              </Card>
              
              <Card className="glass card-hover" data-testid="stat-active">
                <CardContent className="p-6">
                  <Flag className="w-8 h-8 text-accent mb-4" />
                  <p className="text-4xl font-black">{stats?.active_tournaments || 0}</p>
                  <p className="text-sm text-muted-foreground">Active Now</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-muted/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Features</Badge>
            <h2 className="text-4xl sm:text-5xl font-black">
              EVERYTHING YOU NEED
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              From registration to final standings, we've got you covered.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border card-hover group"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Age Categories Section */}
      <section className="py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Age Categories</Badge>
              <h2 className="text-4xl sm:text-5xl font-black mb-6">
                DIVISIONS FOR
                <span className="block text-primary">ALL AGES</span>
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Separate brackets and standings for each age group ensures fair 
                competition. From under-8 youth leagues to adult recreational 
                tournaments.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'].map(category => (
                  <div 
                    key={category}
                    className="px-4 py-3 rounded-lg border bg-card text-center font-bold hover:border-primary transition-colors"
                    data-testid={`category-${category}`}
                  >
                    {category}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_tourneytracker-4/artifacts/mwtsei5w_man-carrying-ball-avoids-defender-in-recreational-flag-football-game.jpg"
                alt="Flag football action"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-accent text-accent-foreground px-6 py-3 rounded-lg font-bold shadow-lg">
                Boys • Girls • Mixed
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-black mb-6">
            READY TO GET STARTED?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Create your first tournament in minutes. Free to start, 
            no credit card required.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" variant="secondary" className="gap-2" data-testid="cta-signup-btn">
                Create Free Account
                <ChevronRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/tournaments">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                data-testid="cta-browse-btn"
              >
                Browse Tournaments
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
